import Vue from 'vue';
import permitPriceService from '@/api/permit-price-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';

const getDefaultPermitPriceFormItem = () => ({});

export const getDefaultPermitPriceFilterParams = () => ({});

const state = {
  permitPrices: null,
  permitPricePagination: {},
  editedPermitPrice: {},
  permitPriceValidationErrors: {},
  permitPriceFilterParams: getDefaultPermitPriceFilterParams(),
  newPermitPrice: getDefaultPermitPriceFormItem(),
};

const getters = {
  drivingPermitTypesMap() {
    return {
      driving_on_apron_initial_permit: {
        label: i18n.t('driving_permit_types.driving_on_apron_initial_permit'),
        value: 'driving_on_apron_initial_permit',
      },
      driving_on_apron_repeated_permit: {
        label: i18n.t('driving_permit_types.driving_on_apron_repeated_permit'),
        value: 'driving_on_apron_repeated_permit',
      },
      driving_on_maneuvering_area: {
        label: i18n.t('driving_permit_types.driving_on_maneuvering_area'),
        value: 'driving_on_maneuvering_area',
      },
    };
  },

    KZonePermitTypesMap() {
    return {
      permanent_k_zone_permit: {
        label: i18n.t('k_zone_permit_types.permanent_k_zone_permit'),
        value: 'permanent_k_zone_permit',
      },
    };
  },

  KZonePermitTypes(state, getters) {
    return Object.values(getters.KZonePermitTypesMap);
  },

  drivingPermitTypes(state, getters) {
    return Object.values(getters.drivingPermitTypesMap);
  },
};

const mutations = {
  SET_PERMIT_PRICES(state, { data, current_page, per_page, total }) {
    state.permitPrices = data;
    state.permitPricePagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_PERMIT_PRICE_FILTER_PARAMS(state, params) {
    state.permitPriceFilterParams = params;
  },

  SET_EDITED_PERMIT_PRICE(state, permitPrice) {
    state.permitPriceValidationErrors = {};
    state.editedPermitPrice = JSON.parse(JSON.stringify(permitPrice));
  },

  STORE_PERMIT_PRICE(state, permitPrice) {
    state.permitPrices = [...state.permitPrices, permitPrice];
    state.permitPricePagination.total += 1;
    state.permitPriceValidationErrors = {};
    state.newPermitPrice = getDefaultPermitPriceFormItem();
  },

  UPDATE_PERMIT_PRICE(state, permitPrice) {
    state.permitPrices = updateArrayItem(state.permitPrices, permitPrice);
  },

  DELETE_PERMIT_PRICE(state, permitPrice) {
    state.permitPrices = removeArrayItem(state.permitPrices, permitPrice);
    state.permitPricePagination.total -= 1;
  },

  SET_PERMIT_PRICE_VALIDATION_ERRORS(state, permitPriceValidationErrors) {
    state.permitPriceValidationErrors = permitPriceValidationErrors;
  },

  RESET_PERMIT_PRICE_VALIDATION_ERRORS(state) {
    state.permitPriceValidationErrors = {};
  },

  CLEAR_PERMIT_PRICE_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.permitPriceValidationErrors, field);
  },
};

const actions = {
  fetchPermitPrices({ commit }, params) {
    commit('SET_PERMIT_PRICE_FILTER_PARAMS', params);
    return permitPriceService.getPage(params).then((res) => {
      commit('SET_PERMIT_PRICES', res.data);
      if (params.page !== state.permitPriceFilterParams.page) {
        window.scrollTo(0, 0);
      }
      return res.data;
    });
  },

  storePermitPrice({ getters, commit }, permitPrice) {
    const isDrivingPermit = Object.keys(getters.drivingPermitTypesMap).includes(permitPrice.type);
    return permitPriceService
      .create(permitPrice)
      .then((res) => {
        commit('STORE_PERMIT_PRICE', res.data);
        eventBus.$emit(
          OPEN_SNACKBAR,
          i18n.t(isDrivingPermit ? 'driving_permit_price_created' : 'permit_price_created'),
        );
        return res.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_PRICE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editPermitPrice({ state, commit }, permitPriceId) {
    const permitPrice = state.permitPrices?.find((c) => c.id === permitPriceId);
    if (permitPrice) {
      commit('SET_EDITED_PERMIT_PRICE', permitPrice);
      return Promise.resolve(permitPrice);
    }
    return permitPriceService.getById(permitPriceId).then((res) => {
      commit('SET_EDITED_PERMIT_PRICE', res.data);
      return res.data;
    });
  },

  updatePermitPrice({ getters, commit }, permitPrice) {
    const isDrivingPermit = Object.keys(getters.drivingPermitTypesMap).includes(permitPrice.type);

    return permitPriceService
      .update(permitPrice)
      .then((res) => {
        commit('UPDATE_PERMIT_PRICE', res.data);
        eventBus.$emit(
          OPEN_SNACKBAR,
          i18n.t(isDrivingPermit ? 'driving_permit_price_updated' : 'permit_price_updated'),
        );
        return res.data;
      })
      .catch((err) => {
        commit('SET_PERMIT_PRICE_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  clearPermitPriceValidationErrors({ commit }, field) {
    commit('CLEAR_PERMIT_PRICE_VALIDATION_ERRORS', field);
  },

  deletePermitPrice({ getters, commit }, permitPrice) {
    const isDrivingPermit = Object.keys(getters.drivingPermitTypesMap).includes(permitPrice.type);

    openConfirmDialog({
      title: i18n.t(
        isDrivingPermit ? 'confirm_driving_permit_price_delete' : 'confirm_permit_price_delete',
      ),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      permitPriceService.delete(permitPrice).then(() => {
        commit('DELETE_PERMIT_PRICE', permitPrice);
        eventBus.$emit(
          OPEN_SNACKBAR,
          i18n.t(isDrivingPermit ? 'driving_permit_price_deleted' : 'permit_price_deleted'),
        );
      });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
